import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import { FaDatabase, FaDownload, FaPlus, FaSyncAlt } from "react-icons/fa";

type BackupItem = Record<string, any> & {
  id?: string;
  name?: string;
  fileName?: string;
  filename?: string;
  createdAt?: string;
  created_at?: string;
  timestamp?: string;
  date?: string;
  time?: string;
};

type ErrorState = any;

const formatDateTime = (value: string | Date | null | undefined, locale = "nl-NL") => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
};

const deriveFilename = (res: any) => {
  const cd = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"];
  if (typeof cd === "string") {
    const match = cd.match(/filename="?([^"]+)"?/i);
    if (match?.[1]) return match[1];
  }
  return `backup_${new Date().toISOString().replace(/[:.]/g, "-")}.sql`;
};

const AdminBackups = () => {
  const { t, i18n } = useTranslation();
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<ErrorState | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null); // name/id currently downloading
  const [refreshing, setRefreshing] = useState(false);
  const [listSupported, setListSupported] = useState(true);

  const lastBackupAt = useMemo(() => {
    const times = backups
      .map((b) => b.createdAt || b.created_at || b.timestamp || b.date || b.time)
      .filter(Boolean)
      .map((v) => new Date(v))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => b.getTime() - a.getTime());
    return times[0] || null;
  }, [backups]);

  const fetchBackups = async (opts = { refresh: false }) => {
    try {
      setErr(null);
      if (opts.refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const { data } = await http.get("/admin/backups");
      setBackups(Array.isArray(data) ? data : []);
      setListSupported(true);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setListSupported(false);
        setBackups([]);
      } else {
        setErr(mapHttpErrorToMessage(e, t));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBackups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const triggerBackup = async () => {
    setTriggering(true);
    setErr(null);
    try {
      await http.post("/admin/backups");
      fetchBackups({ refresh: true });
    } catch (e: any) {
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (typeof e?.message === "string" ? e.message : null);
      setErr(
        mapHttpErrorToMessage(e, t) || {
          message:
            detail ||
            t("admin.backups.trigger_failed", {
              defaultValue: "Backup creation failed. Is pg_dump available on the server?",
            }),
        }
      );
    } finally {
      setTriggering(false);
    }
  };

  const downloadExisting = async (item: BackupItem) => {
    const name = item?.name || item?.fileName || item?.filename || item?.id;
    if (!name) return;
    setDownloading(name);
    setErr(null);
    try {
      const res = await http.get(`/admin/backups/${encodeURIComponent(name)}`, {
        responseType: "blob",
      });
      const filename = deriveFilename(res) || name;
      downloadBlob(res.data, filename);
    } catch (e: any) {
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (typeof e?.message === "string" ? e.message : null);
      setErr(
        mapHttpErrorToMessage(e, t) || {
          message: detail || t("admin.backups.download_failed", { defaultValue: "Download failed." }),
        }
      );
    } finally {
      setDownloading(null);
    }
  };

  if (loading && listSupported) {
    return <Spinner />;
  }

  if (err && !Array.isArray(backups)) {
    return <ErrorPage message={err?.message || err?.title || err || "Error"} status={err?.status} onRetry={() => fetchBackups()} />;
  }

  const lastBackupLabel = lastBackupAt ? formatDateTime(lastBackupAt, i18n.language) : t("common.unknown", { defaultValue: "Unknown" });

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaDatabase}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={t("admin.backups.title", { defaultValue: "Database Backups" })}
        description={t("admin.backups.description", {
          defaultValue: "Download en beheer database back-ups.",
        })}
        actions={
          <button
            type="button"
            onClick={triggerBackup}
            disabled={triggering}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
          >
            <FaPlus aria-hidden="true" />
            {triggering
              ? t("admin.backups.triggering", { defaultValue: "Creating backup…" })
              : t("admin.backups.trigger", { defaultValue: "Create & download backup" })}
          </button>
        }
      />

      <div className="space-y-6 text-slate-950 dark:text-white">
        <div className="lds-overview-card-enter rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-950 dark:text-white">
                {t("admin.backups.last_backup", { defaultValue: "Last backup" })}: {lastBackupLabel}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("admin.backups.hint", {
                  defaultValue: "Create & store a backup, then download it from the list below.",
                })}
              </p>
              {!listSupported && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t("admin.backups.list_not_supported", { defaultValue: "Listing backups not supported by backend." })}
                </p>
              )}
            </div>
            <span className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm font-bold text-indigo-700 shadow-sm dark:border-indigo-500/30 dark:bg-indigo-900/40 dark:text-indigo-200">
              {backups.length} {t("common.items", { defaultValue: "items" })}
            </span>
          </div>
        </div>

        {err && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
            {err?.message || err?.title || err}
          </div>
        )}

        {listSupported && (
          <div className="lds-overview-card-enter space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
                {t("admin.backups.available", { defaultValue: "Available backups" })}
              </h3>
                <button
                  type="button"
                  onClick={() => fetchBackups({ refresh: true })}
                  disabled={refreshing}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:text-indigo-200"
                >
                <FaSyncAlt aria-hidden="true" className={refreshing ? "animate-spin" : ""} />
                  {refreshing
                    ? t("common.loading", { defaultValue: "Loading…" })
                    : t("common.actions.refresh", { defaultValue: "Refresh" })}
                </button>
            </div>
            {backups.length === 0 ? (
              <div className="rounded-[24px] border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-300">
                {t("admin.backups.none", { defaultValue: "No backups found." })}
              </div>
            ) : (
              <LdsTableCard className="overflow-hidden">
                <LdsTableScroll>
                  <LdsTable>
                    <LdsTableHead>
                      <LdsTableRow>
                        <LdsTableHeadCell>{t("admin.backups.columns.name", { defaultValue: "Backup" })}</LdsTableHeadCell>
                        <LdsTableHeadCell>{t("admin.backups.columns.created", { defaultValue: "Created" })}</LdsTableHeadCell>
                        <LdsTableHeadCell className="text-right">{t("common.actions.name", { defaultValue: "Actions" })}</LdsTableHeadCell>
                      </LdsTableRow>
                    </LdsTableHead>
                    <LdsTableBody>
                {backups.map((b) => {
                  const name = b.name || b.fileName || b.filename || b.id || "(backup)";
                  const created = b.createdAt || b.created_at || b.timestamp || b.date || null;
                  return (
                    <LdsTableRow key={name}>
                      <LdsTableCell className="font-semibold text-slate-950 dark:text-white">{name}</LdsTableCell>
                      <LdsTableCell>{created ? formatDateTime(created, i18n.language) : "—"}</LdsTableCell>
                      <LdsTableCell className="text-right">
                      <button
                        type="button"
                        onClick={() => downloadExisting(b)}
                        disabled={downloading === name}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-xs font-bold text-indigo-700 transition hover:border-indigo-200 hover:bg-indigo-100 disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-900/40 dark:text-indigo-200"
                      >
                        <FaDownload aria-hidden="true" />
                        {downloading === name
                          ? t("common.loading", { defaultValue: "Loading…" })
                          : t("common.actions.download", { defaultValue: "Download" })}
                      </button>
                      </LdsTableCell>
                    </LdsTableRow>
                  );
                })}
                    </LdsTableBody>
                  </LdsTable>
                </LdsTableScroll>
              </LdsTableCard>
            )}
          </div>
        )}
      </div>
    </LdsOverviewPage>
  );
};

export default AdminBackups;
